import Grid from '@mui/material/Grid'
import capitalize from 'lodash/capitalize'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'

import AddressLatLong from 'components/common/address/AddressLatLong'
import AddressRowOne from 'components/common/address/AddressRowOne'
import AddressRowTwo from 'components/common/address/AddressRowTwo'
import { DEFAULT_STATE } from 'components/common/address/model'
import TagsWrapper from 'components/customers/add/tab-content/TagsWrapper'
import { HHFormTextField } from 'components/form-fields/v5'
import TagsSelect from 'components/tags/TagsSelect'
import T from 'T'
import { getAddressParams } from 'utils/helper'

const { addressNamePlaceholder } = DEFAULT_STATE

const AddressForm = ({ metaTags = [], setLatLngForMap, setLatLngForMapHistory }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const { address: addressErrors } = errors
  const watchLine1 = watch('address.line1')
  const watchCity = watch('address.city')
  const watchState = watch('address.state')
  const watchZipCode = watch('address.zipCode')
  const watchLat = watch('address.latitude')
  const watchLong = watch('address.longitude')

  const handleAddressChange = useCallback(
    address => {
      const { line1, city, state, zipCode, country, longitudeFixed, latitudeFixed } = getAddressParams(address)
      setValue('address.line1', line1)
      setValue('address.city', city)
      setValue('address.state', state)
      setValue('address.country', country)
      setValue('address.zipCode', zipCode)
      setValue('address.latitude', latitudeFixed)
      setValue('address.longitude', longitudeFixed)
      setLatLngForMap({ longitudeForMap: longitudeFixed, latitudeForMap: latitudeFixed })
      setLatLngForMapHistory({ longitudeForMapHistory: longitudeFixed, latitudeForMapHistory: latitudeFixed })
    },
    [setValue, setLatLngForMap, setLatLngForMapHistory]
  )

  const handleLine1Change = useCallback(newValue => setValue('address.line1', newValue), [setValue])

  const handleLatBlur = useCallback(
    value => {
      setLatLngForMap(prev => ({ ...prev, latitudeForMap: value }))
      setLatLngForMapHistory(prev => ({ ...prev, latitudeForMapHistory: value }))
    },
    [setLatLngForMap, setLatLngForMapHistory]
  )

  const handleLongBlur = useCallback(
    value => {
      setLatLngForMap(prev => ({ ...prev, longitudeForMap: value }))
      setLatLngForMapHistory(prev => ({ ...prev, longitudeForMapHistory: value }))
    },
    [setLatLngForMap, setLatLngForMapHistory]
  )

  const handleTagsChange = useCallback(
    (e, selectedOptions) => {
      setValue('tags', selectedOptions)
      // Trigger resize to fix map height
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200)
    },
    [setValue]
  )

  return (
    <Grid container height="fit-content" rowGap={2} size={12}>
      <Grid size={12}>
        <HHFormTextField
          fullWidth
          control={control}
          deprecatedLabel={false}
          label={capitalize(T.SERVICE_LOCATION_NAME)}
          name="address.addressName"
          placeholder={addressNamePlaceholder}
        />
      </Grid>
      <AddressRowOne
        showParcelNumber
        control={control}
        hasError={{ line1: addressErrors?.line1 && !watchLine1 }}
        line1={watchLine1}
        onBillingAddressChange={handleAddressChange}
        onLine1Change={handleLine1Change}
      />
      <AddressRowTwo
        control={control}
        hasError={{
          city: addressErrors?.city && !watchCity,
          state: addressErrors?.state && !watchState,
          zipCode: addressErrors?.zipCode && !watchZipCode,
        }}
      />
      <AddressLatLong
        control={control}
        hasLatError={addressErrors?.latitude}
        hasLongError={addressErrors?.longitude}
        lat={watchLat}
        long={watchLong}
        onBlurLat={handleLatBlur}
        onBlurLong={handleLongBlur}
      />
      <Grid size={12}>
        <TagsWrapper>
          <TagsSelect
            deprecatedLabel={false}
            label={capitalize(T.LOCATION_TAGS)}
            limit={2}
            selectedTags={watch('tags')}
            tags={metaTags.filter(data => data.active && data.forLocation)}
            onChange={handleTagsChange}
          />
        </TagsWrapper>
      </Grid>
    </Grid>
  )
}

AddressForm.propTypes = {
  metaTags: PropTypes.array,
  setLatLngForMap: PropTypes.func.isRequired,
  setLatLngForMapHistory: PropTypes.func.isRequired,
}

export default AddressForm
